import { useEffect, useState, useMemo } from 'react';
import { Calendar, Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { format } from 'date-fns';

interface SchedulePreviewProps {
  cronExpression: string;
  timezone: string;
  count?: number;
}

export function SchedulePreview({
  cronExpression,
  timezone,
  count = 5
}: SchedulePreviewProps) {
  const [nextRuns, setNextRuns] = useState<Date[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Debounce the calculation to avoid flickering
    const timer = setTimeout(() => {
      if (!cronExpression || cronExpression.split(' ').length !== 5) {
        setError('Invalid cron expression');
        setNextRuns([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // For now, we'll use a simple client-side calculation
        // In production, this should call the backend API
        const runs = calculateNextRuns(cronExpression, timezone, count);
        setNextRuns(runs);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to calculate schedule');
        setNextRuns([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [cronExpression, timezone, count]);

  // Memoize the formatted runs to prevent re-renders
  const formattedRuns = useMemo(() => {
    return nextRuns.map((date) => ({
      date,
      formatted: format(date, 'EEEE, MMMM d, yyyy'),
      time: format(date, 'h:mm a'),
      relativeTime: getRelativeTimeStatic(date)
    }));
  }, [nextRuns]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (nextRuns.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Enter a valid cron expression to preview schedule
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Calendar className="h-4 w-4" />
        <span>Next {count} Executions</span>
      </div>

      <div className="space-y-2">
        {formattedRuns.map((run, index) => (
          <div
            key={`${run.date.getTime()}-${index}`}
            className="flex items-center justify-between rounded-lg border p-3"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
                {index + 1}
              </div>
              <div>
                <p className="font-medium">
                  {run.formatted}
                </p>
                <p className="text-sm text-muted-foreground">
                  {run.time} {timezone.split('/')[1]}
                </p>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              {run.relativeTime}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper function to calculate next runs (simplified client-side version)
// In production, this should be replaced with API call
function calculateNextRuns(cron: string, _timezone: string, count: number): Date[] {
  try {
    const parts = cron.split(' ');
    if (parts.length !== 5) {
      throw new Error('Invalid cron expression');
    }

    const [minute, hour, dayOfMonth, _month, dayOfWeek] = parts;
    const runs: Date[] = [];
    const now = new Date();

    // Simple implementation for common patterns
    // For production, use a proper cron parser library like cronstrue

    let current = new Date(now);
    current.setSeconds(0);
    current.setMilliseconds(0);

    // Parse hour and minute
    const targetHour = hour === '*' ? 0 : parseInt(hour);
    const targetMinute = minute === '*' ? 0 : parseInt(minute);

    for (let i = 0; i < count * 10 && runs.length < count; i++) {
      current.setHours(targetHour);
      current.setMinutes(targetMinute);

      // Daily pattern
      if (dayOfMonth === '*' && dayOfWeek === '*') {
        if (current > now) {
          runs.push(new Date(current));
        }
        current.setDate(current.getDate() + 1);
      }
      // Weekly pattern
      else if (dayOfMonth === '*' && dayOfWeek !== '*') {
        const targetDay = parseInt(dayOfWeek);
        while (current.getDay() !== targetDay || current <= now) {
          current.setDate(current.getDate() + 1);
        }
        runs.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      // Monthly pattern
      else if (dayOfMonth !== '*' && dayOfWeek === '*') {
        const targetDate = parseInt(dayOfMonth);
        current.setDate(targetDate);
        if (current <= now) {
          current.setMonth(current.getMonth() + 1);
        }
        runs.push(new Date(current));
        current.setMonth(current.getMonth() + 1);
      }
    }

    return runs;
  } catch (err) {
    console.error('Error calculating next runs:', err);
    return [];
  }
}

// Helper to format relative time (static calculation to prevent re-renders)
function getRelativeTimeStatic(date: Date): string {
  const now = new Date();
  const diff = date.getTime() - now.getTime();

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `in ${days} day${days > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `in ${hours} hour${hours > 1 ? 's' : ''}`;
  } else if (minutes > 0) {
    return `in ${minutes} minute${minutes > 1 ? 's' : ''}`;
  } else {
    return 'soon';
  }
}
